/**
 * Dynamically load a JavaScript file
 * Replaces API.loadJS()
 *
 * @param {string} src - The URL of the script to load
 * @param {Object} options - Optional configuration
 * @param {boolean} options.async - Load script asynchronously (default: true)
 * @param {boolean} options.defer - Defer script execution (default: false)
 * @param {string} options.id - Optional ID for the script tag
 * @returns {Promise} Resolves when script is loaded, rejects on error
 */
export function loadScript(src, options = {}) {
	const {
		async = true,
		defer = false,
		id = null
	} = options;

	return new Promise((resolve, reject) => {
		// Check if script is already loaded
		if (id && document.getElementById(id)) {
			resolve();
			return;
		}

		// Check if script with same src is already loaded
		const existingScript = document.querySelector(`script[src="${src}"]`);
		if (existingScript) {
			// If script is already loaded, resolve immediately
			if (existingScript.dataset.loaded === 'true') {
				resolve();
			} else {
				// If script is loading, wait for it
				existingScript.addEventListener('load', resolve);
				existingScript.addEventListener('error', reject);
			}
			return;
		}

		// Create new script element
		const script = document.createElement('script');
		script.src = src;
		script.async = async;
		script.defer = defer;

		if (id) {
			script.id = id;
		}

		script.addEventListener('load', () => {
			script.dataset.loaded = 'true';
			resolve();
		});

		script.addEventListener('error', () => {
			reject(new Error(`Failed to load script: ${src}`));
		});

		// Append to document head
		document.head.appendChild(script);
	});
}

/**
 * Load multiple scripts in parallel
 * @param {Array<string|Object>} scripts - Array of script URLs or config objects
 * @returns {Promise} Resolves when all scripts are loaded
 */
export async function loadScripts(scripts) {
	const promises = scripts.map(script => {
		if (typeof script === 'string') {
			return loadScript(script);
		}
		return loadScript(script.src, script.options);
	});

	return Promise.all(promises);
}

